import React from 'react';
import QuestionnaireItemContent from './questionnaire-item-content';
import { optionDisplay, optionScore, parsePromptSlots } from '../../utils/questionnaire-responses';
import { slateToPlainText } from '../../utils/slate-plain-text';
import styles from '../../style/examinee/questionnaire-test-page.module.css';

/**
 * Renders a single item card in the questionnaire.
 * Shows the item content (Slate document) and response controls.
 *
 * `readOnly` shows the item to someone who is not the one answering it (PIN-324):
 * the controls take no input, and `answered` -- true or false for an item that
 * takes an answer, undefined for one that does not -- is spelled out.
 */
const QuestionnaireItem = ({
  item,
  document,
  responses,
  guesses,
  onResponseChange,
  onGuessChange,
  onUpdateData,
  index,
  allowGuess = false,
  readOnly = false,
  answered
}) => {
  const allowedResponses = item.allowedResponses || [];
  const responseKey = item.responseKey || `response_${item._id}`;
  const currentResponse = responses[responseKey];
  // A guess annotates a chosen response, so the control stays disabled until
  // one of the options is selected — "Before they can select Guess, they must
  // select one of the other four options." (PIN-179 #2.)
  const hasResponse = String(currentResponse ?? '').length > 0;
  const isGuess = Boolean(guesses && guesses[item._id]);

  // Multi-slot sentence-completion items: the prompt carries runs of 3+ underscores,
  // one per response slot. Each slot gets its own textarea keyed `<responseKey>#<i>`,
  // matching the wici-api report's sub-lettering. Single-response items keep one
  // textarea under the bare key. (PIN-152 #5.)
  const slots = item.responseType === 'text' ? parsePromptSlots(slateToPlainText(document)) : null;
  const isMultiSlot = Array.isArray(slots);

  // A single free-text textarea that saves on blur only when the value changed.
  const renderTextarea = (key, ariaLabel, testId) => (
    <textarea
      className={styles.textResponse}
      defaultValue={responses[key] ?? ''}
      rows={3}
      aria-label={ariaLabel}
      data-testid={testId}
      readOnly={readOnly}
      onBlur={(event) => {
        const value = event.target.value;
        if (value !== (responses[key] ?? '')) {
          onResponseChange(key, value, value);
        }
      }}
    />
  );

  return (
    <div className={styles.itemCard} data-testid={`questionnaire-item-${index}`}>
      <div className={styles.itemContent}>
        {item.label && <span className={styles.itemLabel}>{item.label}</span>}
        {/* Multi-slot prompts are rendered as labelled slots below, so suppress the
            raw `___`-laden Slate prompt render here. */}
        {!isMultiSlot && (
          <QuestionnaireItemContent
            document={document}
            responses={responses}
            onUpdateData={readOnly ? undefined : onUpdateData}
          />
        )}
      </div>
      {readOnly && answered !== undefined && (
        <div
          className={answered ? styles.itemStatusAnswered : styles.itemStatusUnanswered}
          data-testid={`questionnaire-item-${index}-status`}
        >
          {answered ? 'Answered' : 'Not answered yet'}
        </div>
      )}

      {isMultiSlot ? (
        <div className={styles.responseSection}>
          {slots.map((slot) => {
            const slotKey = `${responseKey}#${slot.index}`;
            return (
              <div key={slot.index} className={styles.slot}>
                {slot.label && (
                  <div
                    className={styles.responseLabel}
                    data-testid={`questionnaire-item-${index}-slot-${slot.index}-label`}
                  >
                    {slot.label}
                  </div>
                )}
                {renderTextarea(
                  slotKey,
                  slot.label ? `Response for ${slot.label}` : `Response ${slot.index + 1}`,
                  `questionnaire-item-${index}-slot-${slot.index}-response`
                )}
              </div>
            );
          })}
        </div>
      ) : item.responseType === 'text' ? (
        <div className={styles.responseSection}>
          {renderTextarea(
            responseKey,
            item.label ? `Response for ${item.label}` : 'Your response',
            `questionnaire-item-${index}-text-response`
          )}
        </div>
      ) : (
        allowedResponses.length > 0 && (
          <div className={styles.responseSection}>
            <div className={styles.responseLabel}>Select your response:</div>
            <div
              className={styles.radioGroup}
              data-testid={`questionnaire-item-${index}-radio-group`}
            >
              {allowedResponses.map((option, i) => {
                const scoreValue = optionScore(option);
                const label =
                  typeof option === 'object'
                    ? option.response || option.label || option.value
                    : option;
                const responseStr = optionDisplay(option);
                const isSelected = currentResponse === responseStr;

                return (
                  <label
                    key={i}
                    className={isSelected ? styles.radioOptionSelected : styles.radioOption}
                  >
                    <input
                      type="radio"
                      name={responseKey}
                      value={responseStr}
                      checked={isSelected}
                      disabled={readOnly}
                      onChange={() => onResponseChange(responseKey, responseStr, scoreValue)}
                      data-testid={`questionnaire-item-${index}-radio-${responseStr}`}
                    />
                    {label}
                  </label>
                );
              })}
              {allowGuess && (
                <button
                  type="button"
                  className={isGuess ? styles.guessButtonSelected : styles.guessButton}
                  disabled={readOnly || !hasResponse}
                  aria-pressed={isGuess}
                  data-testid={`questionnaire-item-${index}-guess`}
                  onClick={() => onGuessChange(item._id, !isGuess)}
                >
                  Guess
                </button>
              )}
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default QuestionnaireItem;
